import { STAGES } from "../constants/dealStages";

/**
 * Stage-change slips, read out of the activity log.
 *
 * Every stage change logs one `stage_changed` row, so a misclick logs a stage
 * the deal never really reached. The stage menus list STAGES in order, which
 * puts In Diligence directly above Passed: on 7/21 five deals went to In
 * Diligence and on to Passed within 14 seconds each, and every funnel counted
 * them as having reached diligence.
 *
 * Two changes on one deal within SLIP_WINDOW_MS, the second picking up where
 * the first left off, are read as a slip and corrected:
 *
 *   A -> B -> A   moved and moved straight back. The deal never left A; the
 *                 pair becomes one A -> A, which keeps the proof it was in A.
 *   A -> B -> C   B sits next to C in the menu - the row above or below the
 *                 one meant. The pair becomes one A -> C.
 *
 * A quick A -> B -> C where B is NOT C's neighbour is left alone: that is
 * somebody recording history after the fact (Underwriting -> LOI Submitted ->
 * Passed, entered in one sitting), and the LOI was real.
 *
 * The log itself is untouched; this is applied wherever the log is read for
 * history, so every funnel and timing agrees. Backfilled milestones
 * (metadata.backfill) are dated by hand, several to the same instant, and are
 * never slips.
 */
export const SLIP_WINDOW_MS = 5 * 60 * 1000;

const time = (r) => Date.parse(r.created_at);
const neighbours = (a, b) => {
  const i = STAGES.indexOf(a), j = STAGES.indexOf(b);
  return i >= 0 && j >= 0 && Math.abs(i - j) === 1;
};

export function collapseSlips(rows = [], windowMs = SLIP_WINDOW_MS) {
  const out = [];
  const byDeal = new Map();
  for (const r of rows) {
    const isMove = (!r.action || r.action === "stage_changed") && r.metadata && !r.metadata.backfill
      && Number.isFinite(time(r));
    if (!isMove) { out.push(r); continue; }
    if (!byDeal.has(r.entity_id)) byDeal.set(r.entity_id, []);
    byDeal.get(r.entity_id).push(r);
  }
  for (const moves of byDeal.values()) {
    moves.sort((a, b) => time(a) - time(b));
    const kept = [];
    for (const e of moves) {
      const prev = kept[kept.length - 1];
      const b = prev?.metadata.new_stage;
      const from = prev?.metadata.old_stage ?? null;
      const to = e.metadata.new_stage;
      const slip = prev
        && time(e) - time(prev) <= windowMs
        && b && b === e.metadata.old_stage
        && (from === to || neighbours(b, to));
      if (!slip) { kept.push(e); continue; }
      kept.pop();
      kept.push({ ...e, metadata: { ...e.metadata, old_stage: from, slip_of: b } });
    }
    out.push(...kept);
  }
  // Callers read the log oldest-first; keep it that way.
  return out.sort((a, b) => (time(a) || 0) - (time(b) || 0));
}
